import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import BookingItemDay from './BookingItemDay';

function DayView({ resources, startDate, bookings }) {
  const containerRef = useRef(null);
  const firstBookingRef = useRef(null);
  const navigate = useNavigate();
  // Generate hour slots (00:00 to 23:00)
  const hours = Array.from({ length: 24 }, (_, index) => {
    return moment().hours(index).minutes(0).format('HH:mm');
  });

  // Helper function to get bookings for a specific resource
  const getBookingsForResource = (resourceId, date) => {
    return bookings.filter(
      booking => booking.resourceId === resourceId && moment(booking.start).format('YYYY-MM-DD') === date
    );
  };

  // Helper function to calculate booking position and width
  const getBookingStyle = (booking) => {
    const startTime = moment(booking.start).format('HH:mm');
    const endTime = moment(booking.end).format('HH:mm');
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    // Calculate start position (which 15-min slot it starts in)
    const startSlot = startHour * 4 + Math.floor(startMinute / 15);
    
    // Calculate duration in 15-min slots
    const totalStartMinutes = startHour * 60 + startMinute;
    const totalEndMinutes = endHour * 60 + endMinute;
    const durationMinutes = totalEndMinutes - totalStartMinutes;
    const slotsSpan = Math.ceil(durationMinutes / 15);
    
    return { startSlot, slotsSpan };
  };

  const currentDate = startDate.format('YYYY-MM-DD');

  // Find the earliest booking
  const currentDateBookings = bookings.filter(b => b.date === currentDate);
  const earliestBooking = currentDateBookings.length > 0
    ? currentDateBookings.reduce((earliest, booking) => {
        return booking.start < earliest.start ? booking : earliest;
      })
    : null;

  // Scroll to first booking on mount or date change, or to middle of day if no bookings
  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      
      if (firstBookingRef.current) {
        // Scroll to show the first booking with some padding
        const firstBookingElement = firstBookingRef.current;
        const scrollLeft = firstBookingElement.offsetLeft - 300;
        container.scrollLeft = Math.max(0, scrollLeft);
      } else {
        // No bookings - scroll to middle of day (12:00 = slot 48 out of 96)
        const middleSlot = 55;
        const cellWidth = 30; // min-w-[30px]
        const scrollLeft = middleSlot * cellWidth - container.clientWidth / 2;
        container.scrollLeft = Math.max(0, scrollLeft);
      }
    }
  }, [startDate, currentDate]);

  const getBookingId = useCallback(booking => {
  const rawId = booking?._id ?? booking?.id ?? booking?.bookingId ?? booking?.eventId;
  return rawId ? String(rawId) : '';
  }, []);

  const handleBookingNavigate = useCallback(
  booking => {
    const bookingId = getBookingId(booking);
    if (!bookingId) return;
    navigate(`/schedule/details/${bookingId}`);
  },
  [getBookingId, navigate]
  );

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
      <div className="overflow-x-auto" ref={containerRef}>
        <table className="min-w-full">
          {/* Table Header */}
          <thead className="bg-gray-50">
            <tr className="divide-x divide-gray-200">
              <th className="px-5 py-3 text-left text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50 z-20 min-w-[120px] border-b border-gray-200">
                Resource
              </th>
              {hours.map((hour, index) => (
                <th 
                  key={index} 
                  colSpan={4}
                  className="px-2 py-3 text-center text-sm font-medium border-b border-gray-200"
                >
                  {hour}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="bg-white">
            {resources.map((resource) => {
              const resourceBookings = getBookingsForResource(resource.id, currentDate);
              return (
                <tr key={resource.id} className="divide-x divide-gray-200 relative">
                  <td className="px-6 py-2 text-sm font-medium text-gray-900 sticky left-0 bg-white whitespace-nowrap border-b border-gray-200 z-20">
                    {resource.name}
                  </td>
                  {hours.map((hour, hourIndex) => (
                    // Create 4 cells for each hour (15-minute intervals)
                    Array.from({ length: 4 }, (_, quarterIndex) => {
                      const currentSlot = hourIndex * 4 + quarterIndex;
                      
                      // Check if any booking starts in this slot
                      const bookingInSlot = resourceBookings.find(booking => {
                        const { startSlot } = getBookingStyle(booking);
                        return startSlot === currentSlot;
                      });
                      
                      // Check if this is the first booking
                      const isFirstBooking = earliestBooking && bookingInSlot && 
                        bookingInSlot.start === earliestBooking.start &&
                        bookingInSlot.resourceId === earliestBooking.resourceId;
                      
                      return (
                        <td 
                          key={`${hourIndex}-${quarterIndex}`}
                          className="px-1 py-2 text-sm border-b border-gray-200 border-r border-gray-200 overflow-visible relative min-w-[30px] h-16"
                          ref={isFirstBooking ? firstBookingRef : null}
                        >
                          {bookingInSlot && (
                            <div 
                              className="absolute top-1 left-0 h-[calc(100%-8px)] z-10"
                              style={{
                                width: `calc(${getBookingStyle(bookingInSlot).slotsSpan * 100}% + ${(getBookingStyle(bookingInSlot).slotsSpan - 1) * 1}px)`
                              }}
                              onClick={() => handleBookingNavigate(bookingInSlot)}
                            >
                              <BookingItemDay booking={bookingInSlot} />
                            </div>
                          )}
                        </td>
                      );
                    })
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DayView;
